import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  FaCheckCircle,
  FaClock,
  FaClipboardList,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaQrcode,
  FaSignOutAlt,
  FaTimes,
} from "react-icons/fa";

import "./QRPage.css";
import { API_URL } from "../../config";

const QRPage = ({ onLogout }) => {
  const [activity, setActivity] = useState("");
  const [entries, setEntries] = useState([]);

  const [showScanner, setShowScanner] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState(null);

  const scannerRef = useRef(null);
  const scanLockedRef = useRef(false);

  const showStatus = useCallback((type, text) => {
    setMessage({ type, text });

    window.clearTimeout(showStatus.timeoutId);

    showStatus.timeoutId = window.setTimeout(() => {
      setMessage(null);
    }, 4000);
  }, []);

  const parseLocationFromQR = useCallback((decodedText) => {
    try {
      const url = new URL(decodedText);

      return (
        url.searchParams.get("location") ||
        url.searchParams.get("ort") ||
        decodedText
      );
    } catch {
      return decodedText;
    }
  }, []);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;

    if (!scanner) {
      setShowScanner(false);
      scanLockedRef.current = false;
      return;
    }

    scannerRef.current = null;

    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }

      scanner.clear();
    } catch (error) {
      console.error("Scanner konnte nicht beendet werden:", error);
    } finally {
      scanLockedRef.current = false;
      setShowScanner(false);
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setLoadingEntries(false);
      showStatus("error", "Benutzer-ID wurde nicht gefunden.");
      return;
    }

    setLoadingEntries(true);

    try {
      const response = await fetch(
        `${API_URL}/api/entries/${encodeURIComponent(userId)}`
      );

      if (!response.ok) {
        throw new Error("Einträge konnten nicht geladen werden.");
      }

      const data = await response.json();

      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fehler beim Laden der Einträge:", error);
      showStatus("error", "Der Verlauf konnte nicht geladen werden.");
    } finally {
      setLoadingEntries(false);
    }
  }, [showStatus]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const saveEntryToServer = useCallback(
    async (entry) => {
      setSaving(true);

      try {
        const response = await fetch(`${API_URL}/api/entries`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(entry),
        });

        const responseData = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            responseData?.error || "Eintrag konnte nicht gespeichert werden."
          );
        }

        const savedEntry = {
          ...entry,
          ...responseData,
        };

        setEntries((previousEntries) => [
          savedEntry,
          ...previousEntries,
        ]);

        showStatus("success", "Eintrag wurde erfolgreich gespeichert.");

        if ("vibrate" in navigator) {
          navigator.vibrate([100, 50, 100]);
        }

        return true;
      } catch (error) {
        console.error("Fehler beim Speichern:", error);

        showStatus(
          "error",
          error.message || "Eintrag konnte nicht gespeichert werden."
        );

        return false;
      } finally {
        setSaving(false);
      }
    },
    [showStatus]
  );

  const handleScan = useCallback(
    async (decodedText) => {
      if (scanLockedRef.current) {
        return;
      }

      scanLockedRef.current = true;

      const userId = Number(localStorage.getItem("userId"));
      const location = parseLocationFromQR(decodedText);

      if (!userId) {
        showStatus("error", "Benutzer konnte nicht ermittelt werden.");
        await stopScanner();
        return;
      }

      if (!activity.trim()) {
        showStatus("error", "Bitte zuerst eine Tätigkeit eingeben.");
        scanLockedRef.current = false;
        return;
      }

      const entry = {
        code: location,
        time: new Date().toISOString(),
        activity: activity.trim(),
        user_id: userId,
      };

      await stopScanner();

      const wasSaved = await saveEntryToServer(entry);

      if (wasSaved) {
        setActivity("");
      }
    },
    [
      activity,
      parseLocationFromQR,
      saveEntryToServer,
      showStatus,
      stopScanner,
    ]
  );

  useEffect(() => {
    if (!showScanner) {
      return undefined;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        const cameras = await Html5Qrcode.getCameras();

        if (cancelled) {
          return;
        }

        if (!cameras || cameras.length === 0) {
          throw new Error("Keine Kamera gefunden.");
        }

        const rearCamera =
          cameras.find((camera) =>
            /back|rear|environment|rück|hinten|camera 0/i.test(
              camera.label
            )
          ) || cameras[cameras.length - 1];

        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;

        await scanner.start(
          rearCamera.id,
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const minDimension = Math.min(
                viewfinderWidth,
                viewfinderHeight
              );

              const size = Math.floor(
                Math.min(minDimension * 0.72, 300)
              );

              return {
                width: size,
                height: size,
              };
            },
            aspectRatio: 1,
            disableFlip: false,
          },
          handleScan,
          () => {
            // Laufende Scanfehler sind normal und werden ignoriert.
          }
        );
      } catch (error) {
        console.error("Kamera konnte nicht gestartet werden:", error);

        showStatus(
          "error",
          "Die Kamera konnte nicht geöffnet werden. Bitte Kameraberechtigung prüfen."
        );

        setShowScanner(false);
      }
    };

    startCamera();

    return () => {
      cancelled = true;

      const scanner = scannerRef.current;
      scannerRef.current = null;
      scanLockedRef.current = false;

      if (!scanner) {
        return;
      }

      if (scanner.isScanning) {
        scanner
          .stop()
          .catch(() => {})
          .finally(() => {
            try {
              scanner.clear();
            } catch {
              // Scanner wurde bereits entfernt.
            }
          });
      } else {
        try {
          scanner.clear();
        } catch {
          // Scanner wurde bereits entfernt.
        }
      }
    };
  }, [showScanner, handleScan, showStatus]);

  const startScanner = () => {
    if (!activity.trim()) {
      showStatus("error", "Bitte zuerst eine Tätigkeit eingeben.");
      return;
    }

    scanLockedRef.current = false;
    setShowScanner(true);
  };

  const formatDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Unbekannte Zeit";
    }

    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const latestEntry = entries[0];

  return (
    <div className="qr-page">
      <header className="qr-header">
        <div className="qr-brand">
          <img
            src={`${process.env.PUBLIC_URL}/Images/qroof-logo.png`}
            alt="QRoof"
            className="qr-logo"
          />

          <div>
            <p className="qr-eyebrow">QRoof</p>
            <h1>QR-Zeiterfassung</h1>
          </div>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={onLogout}
        >
          <FaSignOutAlt />
          <span>Logout</span>
        </button>
      </header>

      <main className="qr-content">
        {message && (
          <div
            className={`status-message status-${message.type}`}
            role="status"
          >
            {message.type === "success" ? (
              <FaCheckCircle />
            ) : (
              <FaExclamationTriangle />
            )}

            <span>{message.text}</span>

            <button
              type="button"
              aria-label="Meldung schließen"
              onClick={() => setMessage(null)}
            >
              <FaTimes />
            </button>
          </div>
        )}

        <section className="welcome-section">
          <p className="section-label">Arbeitsbereich</p>
          <h2>Arbeitsort erfassen</h2>
          <p>
            Tätigkeit eintragen und anschließend den QR-Code am
            Einsatzort scannen.
          </p>
        </section>

        {latestEntry && (
          <section className="latest-entry-card">
            <div className="card-icon card-icon-location">
              <FaMapMarkerAlt />
            </div>

            <div className="latest-entry-content">
              <span>Letzter Eintrag</span>
              <strong>{latestEntry.code}</strong>

              <p>
                <FaClock />
                {formatDate(latestEntry.time)}
              </p>
            </div>
          </section>
        )}

        <section className="activity-card">
          <div className="card-heading">
            <div className="card-icon">
              <FaClipboardList />
            </div>

            <div>
              <span>Aktuelle Tätigkeit</span>
              <h2>Was wird erledigt?</h2>
            </div>
          </div>

          <label htmlFor="activityInput">
            Tätigkeit
          </label>

          <textarea
            id="activityInput"
            value={activity}
            onChange={(event) => setActivity(event.target.value)}
            placeholder="Zum Beispiel: Dachrinne gereinigt"
            maxLength={500}
            rows={4}
            disabled={saving}
          />

          <div className="input-footer">
            <span>{activity.length}/500 Zeichen</span>
          </div>

          <button
            type="button"
            className="scan-button"
            onClick={startScanner}
            disabled={saving}
          >
            <FaQrcode />

            <span>
              {saving
                ? "Eintrag wird gespeichert ..."
                : "QR-Code scannen"}
            </span>
          </button>
        </section>

        <section className="history-section">
          <div className="history-heading">
            <div>
              <p className="section-label">Verlauf</p>
              <h2>Erfasste Einträge</h2>
            </div>

            <span className="entry-count">
              {entries.length}
            </span>
          </div>

          {loadingEntries ? (
            <div className="loading-card">
              <div className="loading-spinner" />
              <p>Einträge werden geladen ...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FaQrcode />
              </div>

              <h3>Noch keine Einträge</h3>

              <p>
                Nach dem ersten Scan wird der Eintrag hier angezeigt.
              </p>
            </div>
          ) : (
            <div className="entries-grid">
              {entries.map((entry, index) => (
                <article
                  key={
                    entry.id ||
                    `${entry.time}-${entry.code}-${index}`
                  }
                  className="entry-card"
                >
                  <div className="entry-card-header">
                    <div className="entry-location-icon">
                      <FaMapMarkerAlt />
                    </div>

                    <div className="entry-location">
                      <span>Ort</span>
                      <h3>{entry.code}</h3>
                    </div>
                  </div>

                  <p className="entry-activity">
                    {entry.activity}
                  </p>

                  <div className="entry-time">
                    <FaClock />
                    <span>{formatDate(entry.time)}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {showScanner && (
        <div className="scanner-modal" role="dialog" aria-modal="true">
          <div className="scanner-panel">
            <div className="scanner-header">
              <div>
                <p className="section-label">Kamera</p>
                <h2>QR-Code scannen</h2>
              </div>

              <button
                type="button"
                className="scanner-close-button"
                onClick={stopScanner}
                aria-label="Scanner schließen"
              >
                <FaTimes />
              </button>
            </div>

            <div className="scanner-description">
              Halte den QR-Code vollständig in den markierten Bereich.
            </div>

            <div id="qr-reader" className="scanner-reader" />

            <div className="scanner-activity">
              <span>Aktuelle Tätigkeit</span>
              <strong>{activity}</strong>
            </div>

            <button
              type="button"
              className="cancel-scan-button"
              onClick={stopScanner}
            >
              Scan abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRPage;